import React, { useEffect, useRef } from 'react';
import { parseMinecraftFormatting } from '../../utils/minecraftFormatting';


const ProcessStatusDisplay = ({
  lines,
  status,
  statusColor,
  isLoading,
  // error prop is not used here as RconHackingPage displays it separately
  progressData,
  actions,
  isConsoleInputEnabled,
  onSendCommand,
  consoleTitle = "Process Log",
  consoleMaxHeight = "h-[350px]", // Default max height
  // New props
  className = "",
  showHeader = true,
  showProgressBar = true,
  showActions = true,
  showMainBorder = true,
}) => {
  const consoleAreaRef = useRef(null); // Changed from consoleEndRef for the container
  const commandInputRef = useRef(null);

    // ***** ADD THIS LOG *****
  console.log('[ProcessStatusDisplay] isConsoleInputEnabled:', isConsoleInputEnabled, 'isLoading:', isLoading, 'Status:', status);
  // ***********************

  useEffect(() => {
    if (consoleAreaRef.current) {
      // Scroll the container itself to its bottom
      consoleAreaRef.current.scrollTo({
        top: consoleAreaRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [lines]);

  const handleCommandSubmit = (e) => {
    e.preventDefault();
    if (commandInputRef.current && commandInputRef.current.value.trim() && onSendCommand) {
      onSendCommand(commandInputRef.current.value.trim());
      commandInputRef.current.value = "";
    }
  };

  const getLineStyle = (line) => {
    let className = 'text-gray-300'; // Default
    switch (line.type) {
      case 'success':
        className = 'text-accent-green';
        break;
      case 'error':
        className = 'text-accent-red';
        break;
      case 'warning':
        className = 'text-accent-yellow';
        break;
      case 'info':
        className = 'text-accent-blue';
        break;
      case 'system':
        className = 'text-gray-400'; // Or pink if desired for some system messages
        if (line.text?.toLowerCase().includes("rcon access granted")) {
            className = 'text-accent-green';
        } else if (line.text?.toLowerCase().includes("initiating") || line.text?.toLowerCase().includes("initializing")) {
            className = 'text-accent-pink';
        } else if (line.text?.startsWith("Available password bases")) {
            className = 'text-accent-pink';
        }
        break;
      case 'command':
        className = 'text-accent-pink';
        break;
      default:
        className = 'text-gray-300';
    }
    return { text: line.text, className };
  };

return (
    <div className={`${showMainBorder ? "bg-dark-600 rounded-2xl p-6 border border-dark-400" : ""} flex flex-col h-full ${className}`}>
      {showHeader && (
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-lg flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${isLoading ? 'bg-accent-yellow animate-pulse' : statusColor.includes('green') ? 'bg-accent-green' : statusColor.includes('red') ? 'bg-accent-red' : 'bg-gray-500'}`}></div>
            {consoleTitle}
          </h3>
          <span className={`text-sm font-medium ${statusColor}`}>{status}</span>
        </div>
      )}

      {showProgressBar && progressData && (progressData.total > 0 || progressData.scanned > 0 || isLoading) && ( // Ensure total > 0 for meaningful progress
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>{progressData.scanned}/{progressData.total > 0 ? progressData.total : (isLoading && progressData.scanned === 0 ? '...' : 'N/A')} Processed</span>
            {progressData.found > 0 && <span>Found: {progressData.found}</span>}
          </div>
          <div className="w-full bg-dark-700 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full ${statusColor.includes('red') ? 'bg-accent-red' : 'bg-gradient-to-r from-accent-pink to-accent-purple'} transition-all duration-300 ease-out`}
              style={{ width: `${progressData.total > 0 ? (progressData.scanned / progressData.total) * 100 : (isLoading ? 50 : (progressData.scanned > 0 ? 100 : 0))}%` }}
            ></div>
          </div>
        </div>
      )}

<div
        ref={consoleAreaRef} // Assign ref here
        className={`terminal-output-area bg-dark-800 p-3 rounded-lg overflow-y-auto custom-scrollbar ${consoleMaxHeight} ${isConsoleInputEnabled || (showActions && actions && actions.length > 0) ? 'mb-3' : ''}`}
      >
        {lines.map((line, index) => {
          const styleInfo = getLineStyle(line);
          // Pass the base className from styleInfo to parseMinecraftFormatting.
          // This allows uncolored parts of a formatted string to adopt the line's base color.
          const parsedContent = parseMinecraftFormatting(line.text, styleInfo.className);
          
          return (
            <div key={index} className={`terminal-line mb-1 font-mono text-xs whitespace-pre-wrap ${styleInfo.className}`}>
              {parsedContent}
            </div>
          );
        })}
        {/* <div ref={consoleEndRef} /> */}
      </div>

{isConsoleInputEnabled && (
        <form onSubmit={handleCommandSubmit} className="flex gap-2 mb-5"> {/* Increased bottom margin, e.g., mb-5 (1.25rem) */}
          <input
            ref={commandInputRef}
            type="text"
            placeholder="Enter command..."
            className="flex-grow bg-dark-700 border border-dark-500 rounded-lg px-3 py-2.5 text-sm text-gray-200 focus:ring-accent-purple focus:border-accent-purple custom-input" // Adjusted py for input height
            disabled={!isConsoleInputEnabled || isLoading}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-accent-purple hover:bg-accent-purple/80 text-white rounded-lg text-sm font-medium transition-colors"
            disabled={!isConsoleInputEnabled || isLoading}
          >
            Send
          </button>
        </form>
      )}

      {showActions && actions && actions.length > 0 && (
        <div className={`flex gap-3 ${showMainBorder ? 'pt-3 border-t border-dark-500' : ''} ${isConsoleInputEnabled ? 'mt-0' : 'mt-4'}`}>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2
                ${action.primary ? 'bg-accent-pink hover:bg-accent-pink/80 text-white' : 'bg-dark-500 hover:bg-dark-400 text-gray-300'}
                ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {action.icon && <i className={`fas ${action.icon}`}></i>}
              {action.loading && action.disabled ? action.loadingLabel || 'Processing...' : action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProcessStatusDisplay;